import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { IconPlus, IconRefresh, IconPencil, IconTrash } from '@tabler/icons-react';
import {
  getTaskDropdowns,
  deleteTaskDropdown,
  type TaskDropdownRow,
} from '../services/taskDropdownService';
import { TaskDropdownFormDialog } from '../components/task-lists/TaskDropdownFormDialog';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';

type EditorState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; dropdown: TaskDropdownRow };

export const TaskDropdownsAdminPage: React.FC = () => {
  const [rows, setRows] = useState<TaskDropdownRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const [filter, setFilter] = useState('');

  const filteredRows = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.displayName, row.dropdownId, ...row.options]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [rows, filter]);

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTaskDropdowns(signal);
      setRows(data.dropdowns);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load task dropdowns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  const handleDelete = async (row: TaskDropdownRow) => {
    if (
      !window.confirm(
        `Delete dropdown set "${row.displayName}"? Tasks still referencing it will show an empty dropdown.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await deleteTaskDropdown(row.id ?? row.dropdownId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete dropdown');
    }
  };

  const previewOptions = (options: string[]): string => {
    const shown = options.slice(0, 3).join(', ');
    const extra = options.length - 3;
    return extra > 0 ? `${shown} +${extra} more` : shown || '—';
  };

  return (
    <>
      <SecondaryToolbar floating>
        <Typography sx={{ ml: 0.5, fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
          Task Dropdowns
        </Typography>
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by name, ID, option…"
        />
        <ToolbarSpacer />
        <ToolbarButton variant="primary" icon={IconPlus} onClick={() => setEditor({ mode: 'create' })}>
          Add dropdown set
        </ToolbarButton>
        <ToolbarButton icon={IconRefresh} onClick={() => loadData()}>
          Reload
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Display Name</DataTableHeadCell>
                  <DataTableHeadCell>ID</DataTableHeadCell>
                  <DataTableHeadCell>Options</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <DataTableEmpty colSpan={4}>
                    {filter.trim() ? (
                      <>No dropdown sets match "{filter.trim()}".</>
                    ) : (
                      <>No dropdown sets yet. Click <strong>Add dropdown set</strong> to create one.</>
                    )}
                  </DataTableEmpty>
                ) : (
                  filteredRows.map((row) => (
                    <DataTableRow key={row.dropdownId}>
                      <DataTableCell bold>{row.displayName}</DataTableCell>
                      <DataTableCell muted>{row.dropdownId}</DataTableCell>
                      <DataTableCell muted>
                        {row.options.length} · {previewOptions(row.options)}
                      </DataTableCell>
                      <DataTableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => setEditor({ mode: 'edit', dropdown: row })}
                          >
                            <IconPencil size={16} stroke={1.5} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(row)}>
                            <IconTrash size={16} stroke={1.5} />
                          </IconButton>
                        </Tooltip>
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <TaskDropdownFormDialog
        open={editor.mode !== 'closed'}
        dropdown={editor.mode === 'edit' ? editor.dropdown : null}
        onClose={() => setEditor({ mode: 'closed' })}
        onSaved={() => {
          setEditor({ mode: 'closed' });
          loadData();
        }}
      />
    </>
  );
};
